import { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import {
  ClientApplication,
  useAddClientVersionMutation,
  useClientVersionSummariesQuery,
} from 'schema/serverTypes';
import { FORM_ERROR } from 'final-form';
import { parseVersion } from '../../versionUtils';

export interface AddVersionValues {
  client: ClientApplication;
  major: string;
  minor: string;
  patch: string;
  mandatory: boolean;
}

export const validateVersionPart = (value: string) => {
  if (value === undefined || value === '') {
    return 'Введите число';
  }
  return /^\d+$/.test(value) && Number(value) <= 2147483647
    ? undefined
    : 'Введите целое число от 0 до 2147483647';
};

export const useVersionForm = (onClose: () => void) => {
  const { search } = useLocation();
  const apollo = useApolloClient();
  const [createVersionMutation] = useAddClientVersionMutation();
  const summaries = useClientVersionSummariesQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [created, setCreated] = useState(false);
  const initialValues = useMemo<AddVersionValues>(
    () => ({
      client:
        new URLSearchParams(search).get('client') === ClientApplication.Android
          ? ClientApplication.Android
          : ClientApplication.Ios,
      major: '',
      minor: '',
      patch: '',
      mandatory: false,
    }),
    [search]
  );

  const onSubmit = useCallback(
    async (values: AddVersionValues) => {
      if (!created) {
        const version = parseVersion(`${values.major}.${values.minor}.${values.patch}`);
        const summary = summaries.data?.clientVersionSummaries.find(
          (item) => item.client === values.client
        );
        if (!version || !summary || summaries.loading || summaries.error) {
          return { [FORM_ERROR]: 'Проверьте номер версии и дождитесь загрузки правил платформы.' };
        }
        try {
          const result = await createVersionMutation({
            variables: {
              input: { ...version, client: values.client, mandatory: values.mandatory },
            },
          });
          const payload = result.data?.addClientVersion;
          if (payload?.errors?.length) {
            return {
              [FORM_ERROR]: payload.errors.some(
                (error) => error.__typename === 'ClientVersionAlreadyExistsError'
              )
                ? 'Эта версия уже зарегистрирована для выбранной платформы.'
                : 'Не удалось добавить версию.',
            };
          }
          if (!payload?.version) {
            return { [FORM_ERROR]: 'Сервер не подтвердил добавление версии.' };
          }
          setCreated(true);
        } catch {
          return {
            [FORM_ERROR]:
              'Не удалось получить подтверждение добавления версии. Проверьте соединение. Если запрос дошёл до сервера, повторная попытка сообщит о существующей версии.',
          };
        }
      }
      try {
        await apollo.reFetchObservableQueries();
      } catch {
        return {
          [FORM_ERROR]:
            'Версия добавлена, но перечитать данные не удалось. Повторите обновление данных — версия не будет создана повторно.',
        };
      }
      onClose();
    },
    [
      apollo,
      createVersionMutation,
      created,
      onClose,
      summaries.data,
      summaries.loading,
      summaries.error,
    ]
  );

  return { initialValues, onSubmit, summaries, created };
};
